import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiError, call, type Schemas } from '../api/client';
import { realtime } from '../realtime/client';

export type Session = Schemas['Session'];

/**
 * The signed-in session (`GET /auth/session`): user, roles, permissions (nav filtering, page
 * access), data scopes and action approval policies. `null` when nobody is signed in.
 */
export function useSession() {
  const q = useQuery<Session | null, ApiError>({
    queryKey: ['session'],
    queryFn: async () => {
      try {
        return await call(api.GET('/auth/session'));
      } catch (e) {
        if (e instanceof ApiError && e.status === 401) return null;
        throw e;
      }
    },
    staleTime: 60_000,
  });
  return { session: q.data, loading: q.isLoading, error: q.error, refetch: q.refetch };
}

/** Permission check against the session (`page.*`, `action.*`, `manage.*`, `launcher.*`). */
export function can(session: Session | null | undefined, permission: string): boolean {
  return !!session?.permissions?.includes(permission);
}

/** Approval policy of an action (e.g. action.disable_trading) for the "2 approvals required" notes. */
export function actionPolicy(session: Session | null | undefined, action: string) {
  return session?.action_policies?.find((p) => p.action === action);
}

/**
 * Reload the session after signing in (or a role change): fetches even when nothing is observing
 * the query yet, then opens the live connection.
 */
export function useRefreshSession() {
  const qc = useQueryClient();
  return useCallback(async () => {
    await qc.invalidateQueries({ queryKey: ['session'] });
    const s = await qc.fetchQuery<Session | null>({
      queryKey: ['session'],
      queryFn: async () => {
        try {
          return await call(api.GET('/auth/session'));
        } catch (e) {
          if (e instanceof ApiError && e.status === 401) return null;
          throw e;
        }
      },
      staleTime: 0,
    });
    if (s) realtime.connect();
    return s;
  }, [qc]);
}

export function useSignOut() {
  const qc = useQueryClient();
  return useCallback(async () => {
    try {
      await call(api.POST('/auth/logout'));
    } catch {
      /* already signed out */
    }
    realtime.disconnect();
    qc.clear();
    qc.setQueryData(['session'], null);
  }, [qc]);
}
